import { useEffect, useRef, useState } from "react";

/**
 * Adds "is-visible" (via the returned boolean) once the element has
 * scrolled into view, for the .reveal fade/slide-up in styles.css.
 * Fires once and disconnects - this is decorative entrance motion, not a
 * repeating scroll-spy. Falls back to already-visible if IntersectionObserver
 * isn't available, so content is never hidden behind a broken observer.
 */
export function useReveal<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, visible };
}
